import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export type Locale = "fr" | "en";

type PreferencesState = {
  locale: Locale;
  highContrast: boolean;
  largeText: boolean;
  wheelchairMode: boolean;
};

type KioskPreferencesContextValue = PreferencesState & {
  setLocale: (locale: Locale) => void;
  toggleHighContrast: () => void;
  toggleLargeText: () => void;
  toggleWheelchairMode: () => void;
  tr: (fr: string, en: string) => string;
  accessibilityClassName: string;
};

const STORAGE_KEY = "boba_kiosk_preferences_v1";

const defaultState: PreferencesState = {
  locale: "fr",
  highContrast: false,
  largeText: false,
  wheelchairMode: false,
};

const KioskPreferencesContext = createContext<KioskPreferencesContextValue | undefined>(
  undefined,
);

const canUseDOM = () => typeof window !== "undefined";

const loadPreferences = (): PreferencesState => {
  if (!canUseDOM()) return defaultState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw) as Partial<PreferencesState>;
    return {
      locale: parsed.locale === "en" ? "en" : "fr",
      highContrast: Boolean(parsed.highContrast),
      largeText: Boolean(parsed.largeText),
      wheelchairMode: Boolean(parsed.wheelchairMode),
    };
  } catch {
    return defaultState;
  }
};

export const KioskPreferencesProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [state, setState] = useState<PreferencesState>(() => loadPreferences());

  useEffect(() => {
    if (!canUseDOM()) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    if (!canUseDOM()) return;
    document.documentElement.lang = state.locale;
  }, [state.locale]);

  const value = useMemo<KioskPreferencesContextValue>(() => {
    const accessibilityClassName = [
      state.highContrast ? "a11y-high-contrast" : "",
      state.largeText ? "a11y-large-text" : "",
      state.wheelchairMode ? "a11y-wheelchair" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return {
      ...state,
      setLocale: (locale) => setState((prev) => ({ ...prev, locale })),
      toggleHighContrast: () =>
        setState((prev) => ({ ...prev, highContrast: !prev.highContrast })),
      toggleLargeText: () =>
        setState((prev) => ({ ...prev, largeText: !prev.largeText })),
      toggleWheelchairMode: () =>
        setState((prev) => ({ ...prev, wheelchairMode: !prev.wheelchairMode })),
      tr: (fr, en) => (state.locale === "fr" ? fr : en),
      accessibilityClassName,
    };
  }, [state]);

  return (
    <KioskPreferencesContext.Provider value={value}>
      {children}
    </KioskPreferencesContext.Provider>
  );
};

export const useKioskPreferences = () => {
  const context = useContext(KioskPreferencesContext);
  if (!context) {
    throw new Error("useKioskPreferences must be used within KioskPreferencesProvider");
  }
  return context;
};
